import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeftIcon, LayoutDashboardIcon, PackageIcon, TargetIcon, MessageSquareIcon, SparklesIcon, SettingsIcon, ShieldCheckIcon, SendIcon, CheckIcon } from 'lucide-react';
export type AccessLevel = 'none' | 'view' | 'edit' | 'full';
export interface ModuleDef {
  id: string;
  label: string;
  icon: React.ReactNode;
  caps: Record<AccessLevel, string>;
}
export const MODULES: ModuleDef[] = [{
  id: 'dashboard',
  label: 'Dashboard',
  icon: <LayoutDashboardIcon className="w-4 h-4" data-id="element-1517" />,
  caps: {
    none: 'Hidden',
    view: 'See business overview',
    edit: 'See business overview',
    full: 'See business overview'
  }
}, {
  id: 'catalogue',
  label: 'Catalogue',
  icon: <PackageIcon className="w-4 h-4" data-id="element-1518" />,
  caps: {
    none: 'No access',
    view: 'View items only',
    edit: 'Add, edit & publish items',
    full: 'Add, edit, publish & delete items'
  }
}, {
  id: 'leads',
  label: 'Leads',
  icon: <TargetIcon className="w-4 h-4" data-id="element-1519" />,
  caps: {
    none: 'No access',
    view: 'View leads & AI insights',
    edit: 'Update status, owner & notes',
    full: 'Manage, reassign & export leads'
  }
}, {
  id: 'conversations',
  label: 'Conversations',
  icon: <MessageSquareIcon className="w-4 h-4" data-id="element-1520" />,
  caps: {
    none: 'No access',
    view: 'Read conversations',
    edit: 'Read & reply to customers',
    full: 'Read, reply & manage chats'
  }
}, {
  id: 'ai',
  label: 'AI Assistant',
  icon: <SparklesIcon className="w-4 h-4" data-id="element-1521" />,
  caps: {
    none: 'No access',
    view: 'View AI knowledge',
    edit: 'Teach & edit AI rules',
    full: 'Configure all AI behavior'
  }
}, {
  id: 'settings',
  label: 'Settings',
  icon: <SettingsIcon className="w-4 h-4" data-id="element-1522" />,
  caps: {
    none: 'No access',
    view: 'View settings',
    edit: 'Edit business profile',
    full: 'Manage team, billing & profile'
  }
}];
export type AccessMap = Record<string, AccessLevel>;
export interface TeamMember {
  name: string;
  email: string;
  role: string;
  access: AccessMap;
  canViewContact: boolean;
}
const LEVELS: AccessLevel[] = ['none', 'view', 'edit', 'full'];
const LEVEL_LABEL: Record<AccessLevel, string> = {
  none: 'None',
  view: 'View',
  edit: 'Edit',
  full: 'Full'
};
export const ROLE_PRESETS: {
  id: string;
  label: string;
  desc: string;
  access: AccessMap;
}[] = [{
  id: 'Admin',
  label: 'Admin',
  desc: 'Full access to everything',
  access: {
    dashboard: 'full',
    catalogue: 'full',
    leads: 'full',
    conversations: 'full',
    ai: 'full',
    settings: 'full'
  }
}, {
  id: 'Manager',
  label: 'Manager',
  desc: 'Runs operations, no billing',
  access: {
    dashboard: 'view',
    catalogue: 'full',
    leads: 'full',
    conversations: 'full',
    ai: 'edit',
    settings: 'view'
  }
}, {
  id: 'Sales',
  label: 'Sales',
  desc: 'Works leads & conversations',
  access: {
    dashboard: 'view',
    catalogue: 'view',
    leads: 'edit',
    conversations: 'edit',
    ai: 'view',
    settings: 'none'
  }
}, {
  id: 'Viewer',
  label: 'Viewer',
  desc: 'Read-only access',
  access: {
    dashboard: 'view',
    catalogue: 'view',
    leads: 'view',
    conversations: 'view',
    ai: 'view',
    settings: 'none'
  }
}];
interface InviteMemberModalProps {
  member?: TeamMember | null;
  onClose: () => void;
  onSave: (member: TeamMember) => void;
}
export function InviteMemberModal({
  member,
  onClose,
  onSave
}: InviteMemberModalProps) {
  const isEdit = !!member;
  const [name, setName] = useState(member?.name ?? '');
  const [email, setEmail] = useState(member?.email ?? '');
  const [role, setRole] = useState(member?.role ?? 'Sales');
  const [access, setAccess] = useState<AccessMap>(member?.access ?? ROLE_PRESETS.find(r => r.id === 'Sales')!.access);
  const [canViewContact, setCanViewContact] = useState(member?.canViewContact ?? false);
  const applyPreset = (presetId: string) => {
    const preset = ROLE_PRESETS.find(r => r.id === presetId);
    if (!preset) return;
    setRole(presetId);
    setAccess(preset.access);
  };
  const setModuleLevel = (moduleId: string, level: AccessLevel) => {
    setAccess(prev => ({
      ...prev,
      [moduleId]: level
    }));
    setRole('Custom');
  };
  const handleSave = () => {
    onSave({
      name: name || 'New Member',
      email,
      role,
      access,
      canViewContact
    });
  };
  const mountNode = typeof document !== 'undefined' && document.getElementById('app-screen-root') || (typeof document !== 'undefined' ? document.body : null);
  if (!mountNode) return null;
  return createPortal(<AnimatePresence data-id="element-1523">
      <motion.div initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} exit={{
      x: '100%'
    }} transition={{
      type: 'spring',
      damping: 28,
      stiffness: 250
    }} className="absolute inset-0 z-[110] bg-[#F8FAFC] flex flex-col overflow-hidden" data-id="element-1524">
        {/* Sticky Header */}
        <div className="flex-shrink-0 bg-white border-b border-slate-100" data-id="element-1525">
          <div className="flex items-center gap-2 px-3 py-3" data-id="element-1526">
            <button onClick={onClose} aria-label="Back" className="p-2 -ml-1 text-slate-600 hover:bg-slate-50 rounded-full transition-colors" data-id="element-1527">
              <ChevronLeftIcon className="w-6 h-6" data-id="element-1528" />
            </button>
            <div className="flex-1 min-w-0" data-id="element-1529">
              <h2 className="text-lg font-bold text-dark-navy leading-tight truncate" data-id="element-1530">
                {isEdit ? `Edit access` : 'Invite member'}
              </h2>
              <p className="text-xs text-slate-400 truncate" data-id="element-1531">
                {isEdit ? member?.name : 'Choose a role, then fine-tune access'}
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-4 space-y-5" data-id="element-1532">
          {/* Identity */}
          {!isEdit && <div className="space-y-3" data-id="element-1533">
              <div data-id="element-1534">
                <label className="block text-[11px] font-medium text-slate-400 uppercase tracking-wider mb-1.5 ml-1" data-id="element-1535">
                  Full name
                </label>
                <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g. Layla Ahmed" className="w-full px-4 py-3.5 bg-white border border-slate-200 rounded-2xl text-[14px] outline-none focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo" data-id="element-1536" />
              </div>
              <div data-id="element-1537">
                <label className="block text-[11px] font-medium text-slate-400 uppercase tracking-wider mb-1.5 ml-1" data-id="element-1538">
                  Email
                </label>
                <input value={email} onChange={e => setEmail(e.target.value)} type="email" placeholder="name@email.com" className="w-full px-4 py-3.5 bg-white border border-slate-200 rounded-2xl text-[14px] outline-none focus:ring-2 focus:ring-primary-indigo/20 focus:border-primary-indigo" data-id="element-1539" />
              </div>
            </div>}

          {/* Role presets */}
          <div data-id="element-1540">
            <label className="block text-[11px] font-medium text-slate-400 uppercase tracking-wider mb-2 ml-1" data-id="element-1541">
              Role
            </label>
            <div className="grid grid-cols-2 gap-2" data-id="element-1542">
              {ROLE_PRESETS.map(p => <button key={p.id} onClick={() => applyPreset(p.id)} className={`text-left px-3.5 py-3 rounded-2xl border-2 transition-colors ${role === p.id ? 'border-primary-indigo bg-indigo-50/60' : 'border-slate-200 bg-white'}`} data-id="element-1543">
                  <div className={`text-sm font-bold ${role === p.id ? 'text-primary-indigo' : 'text-dark-navy'}`} data-id="element-1544">
                    {p.label}
                  </div>
                  <div className="text-[11px] text-slate-500 leading-tight mt-0.5" data-id="element-1545">
                    {p.desc}
                  </div>
                </button>)}
            </div>
            {role === 'Custom' && <div className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-ai-purple bg-purple-50 px-2.5 py-1 rounded-full" data-id="element-1546">
                <SparklesIcon className="w-3.5 h-3.5" data-id="element-1547" /> Custom permissions
              </div>}
          </div>

          {/* Permission matrix */}
          <div data-id="element-1548">
            <label className="block text-[11px] font-medium text-slate-400 uppercase tracking-wider mb-2 ml-1" data-id="element-1549">
              Module access
            </label>
            <div className="flex flex-col gap-2.5" data-id="element-1550">
              {MODULES.map(mod => {
              const level = access[mod.id] ?? 'none';
              return <div key={mod.id} className="bg-white border border-slate-200/70 rounded-2xl p-3.5 shadow-[0_1px_3px_rgb(0,0,0,0.03)]" data-id="element-1551">
                    <div className="flex items-center gap-2.5 mb-3" data-id="element-1552">
                      <span className="w-9 h-9 rounded-xl bg-slate-100 text-slate-500 flex items-center justify-center flex-shrink-0" data-id="element-1553">
                        {mod.icon}
                      </span>
                      <div className="min-w-0" data-id="element-1554">
                        <div className="text-sm font-semibold text-dark-navy leading-tight" data-id="element-1555">
                          {mod.label}
                        </div>
                        <div className="text-[12px] text-slate-500 truncate" data-id="element-1556">
                          {mod.caps[level]}
                        </div>
                      </div>
                    </div>
                    <div className="flex bg-slate-100 rounded-xl p-1" data-id="element-1557">
                      {LEVELS.map(lv => <button key={lv} onClick={() => setModuleLevel(mod.id, lv)} className={`flex-1 py-2 text-[13px] font-bold rounded-lg transition-colors ${level === lv ? 'bg-white text-primary-indigo shadow-sm' : 'text-slate-500 active:text-slate-700'}`} data-id="element-1558">
                          {LEVEL_LABEL[lv]}
                        </button>)}
                    </div>

                    {/* Leads privacy capability */}
                    {mod.id === 'leads' && level !== 'none' && <button onClick={() => setCanViewContact(v => !v)} className="mt-3 w-full flex items-center gap-2.5 text-[13px] font-medium text-slate-600 bg-slate-50 rounded-xl px-3 py-2.5" data-id="element-1559">
                        <span className={`w-5 h-5 rounded-md border flex items-center justify-center flex-shrink-0 ${canViewContact ? 'bg-primary-indigo border-primary-indigo' : 'border-slate-300 bg-white'}`} data-id="element-1560">
                          {canViewContact && <CheckIcon className="w-3.5 h-3.5 text-white" data-id="element-1561" />}
                        </span>
                        <ShieldCheckIcon className="w-4 h-4 text-slate-400 flex-shrink-0" data-id="element-1562" />
                        <span className="text-left" data-id="element-1563">
                          Can view customer contact details
                        </span>
                      </button>}
                  </div>;
            })}
            </div>
          </div>
        </div>

        {/* Sticky Footer */}
        <div className="flex-shrink-0 bg-white border-t border-slate-100 px-4 py-3" data-id="element-1564">
          <button onClick={handleSave} className="w-full py-3.5 bg-primary-indigo text-white text-sm font-bold rounded-2xl active:scale-[0.98] transition-transform shadow-sm flex items-center justify-center gap-2" data-id="element-1565">
            {isEdit ? <>
                <CheckIcon className="w-4 h-4" data-id="element-1566" /> Save access
              </> : <>
                <SendIcon className="w-4 h-4" data-id="element-1567" /> Send invite
              </>}
          </button>
        </div>
      </motion.div>
    </AnimatePresence>, mountNode);
}
export function accessSummary(access: AccessMap): string {
  const active = MODULES.filter(m => (access[m.id] ?? 'none') !== 'none').length;
  return `${active} module${active === 1 ? '' : 's'}`;
}